import { useState, useEffect } from 'react';
import { Box, Text, Switch, Button, Loader, createStyles } from '@mantine/core';
import { useSelector } from 'react-redux';
import swal from 'sweetalert';
import { getSmsConsent, updateSmsConsent } from '../../services/smsConsent';
import { LuMessageSquare, LuMail, LuShieldCheck, LuMessageCircle } from 'react-icons/lu';

const useStyles = createStyles(() => ({
  card: {
    background: '#fff',
    borderRadius: 12,
    border: '1px solid #E5E5E5',
    overflow: 'hidden',
  },
  header: {
    padding: '24px 24px 20px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 16,
    borderBottom: '1px solid #f2f2f2',
  },
  headerIcon: {
    width: 44,
    height: 44,
    background: '#FFF3DC',
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  headerTitle: {
    fontFamily: 'Inter',
    fontSize: 18,
    fontWeight: 700,
    color: '#171717',
    lineHeight: '27px',
    marginBottom: 4,
  },
  headerDesc: {
    fontFamily: 'Inter',
    fontSize: 13.5,
    fontWeight: 400,
    color: '#737373',
    lineHeight: '20.25px',
    maxWidth: 600,
  },
  banner: {
    margin: '20px 24px 20px',
    padding: '12px 16px',
    borderRadius: 8,
    border: '1px solid #fce6be',
    background: '#fff8ec',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  bannerText: {
    fontFamily: 'Inter',
    fontSize: 13,
    lineHeight: '19.5px',
  },
  bannerBold: {
    fontFamily: 'Inter',
    fontSize: 13,
    fontWeight: 700,
    color: '#7a2e0e',
  },
  bannerRegular: {
    fontFamily: 'Inter',
    fontSize: 13,
    fontWeight: 400,
    color: '#7a2e0e',
  },
  channelsLabel: {
    padding: '0 24px 10px',
    fontFamily: 'Inter',
    fontSize: 10.5,
    fontWeight: 600,
    color: '#737373',
    textTransform: 'uppercase',
    letterSpacing: '0.88px',
  },
  channelsWrap: {
    padding: '0 24px 20px',
  },
  channelRow: {
    padding: '16px',
    border: '1px solid #E5E5E5',
    borderRadius: 10,
    marginBottom: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    background: '#fff',
    '&:last-child': { marginBottom: 0 },
  },
  channelIcon: {
    width: 36,
    height: 36,
    background: '#F5F5F5',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  channelBody: {
    flex: 1,
  },
  channelTitle: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: 600,
    color: '#171717',
    lineHeight: '21px',
    marginBottom: 2,
  },
  channelDesc: {
    fontFamily: 'Inter',
    fontSize: 12.5,
    fontWeight: 400,
    color: '#737373',
    lineHeight: '18.75px',
  },
  toggle: {
    marginLeft: 'auto',
    flexShrink: 0,
    '& input:checked + * > .mantine-Switch-track, & input:checked ~ .mantine-Switch-track': {
      backgroundColor: '#171717 !important',
    },
    '& .mantine-Switch-track': {
      cursor: 'pointer',
    },
  },
  footer: {
    padding: '16px 24px',
    borderTop: '1px solid #F2F2F2',
    background: '#FAFAFA',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
  },
  footerText: {
    fontFamily: 'Inter',
    fontSize: 12,
    color: '#A3A3A3',
  },
  saveBtn: {
    height: 38,
    borderRadius: 8,
    border: '1px solid #FFBA45',
    background: '#FFBA45',
    fontFamily: 'Inter',
    fontSize: 13,
    fontWeight: 600,
    color: '#171717',
    padding: '0 20px',
    '&:hover': { opacity: 0.85, background: '#FFBA45' },
  },
  loadingWrap: {
    padding: 24,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  loadingText: {
    fontFamily: 'Inter',
    fontSize: 14,
    color: '#667085',
  },
}));

const SmsConsentManager = ({ userId }) => {
  const { classes } = useStyles();
  const { profile } = useSelector((state) => state?.profile);
  const isAdmin = !profile?.user?.role || profile?.user?.role === 'admin';
  const [consent, setConsent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(false);

  useEffect(() => {
    load();
  }, [userId]);

  const load = async () => {
    setLoading(true);
    const data = await getSmsConsent(userId);
    setConsent(data);
    applyConsentToState(data);
    setLoading(false);
  };

  const applyConsentToState = (data) => {
    setSmsEnabled(data?.smsOptIn === true);
    setEmailEnabled(data?.emailOptIn === true);
  };

  const handleSave = async () => {
    const hasAny = smsEnabled || emailEnabled;
    const confirmed = await swal({
      title: hasAny ? 'Confirm Preferences' : 'Confirm Opt-Out',
      text: hasAny
        ? 'This will save your communication preferences with Ogiso Health Inc.'
        : 'This will record that you have opted out of all communications (SMS and Email).',
      icon: 'warning',
      buttons: ['Cancel', hasAny ? 'Yes, save' : 'Yes, opt out'],
      dangerMode: !hasAny,
    });
    if (!confirmed) return;
    setSaving(true);
    const result = await updateSmsConsent({ smsOptIn: smsEnabled, emailOptIn: emailEnabled }, userId);
    if (result) { setConsent(result); applyConsentToState(result); }
    setSaving(false);
  };

  if (loading) {
    return (
      <Box className={classes.loadingWrap}>
        <Loader size="sm" color="yellow" />
        <Text className={classes.loadingText}>Loading preferences…</Text>
      </Box>
    );
  }

  return (
    <Box className={classes.card}>
      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.headerIcon}>
          <LuMessageCircle size={22} color="#B54708" />
        </Box>
        <Box>
          <Text className={classes.headerTitle}>How we reach you</Text>
          <Text className={classes.headerDesc}>
            Choose the ways you&apos;d like to hear from us about your account. Turn a channel on or
            off anytime — you&apos;re always in control.
          </Text>
        </Box>
      </Box>

      {/* Anti-spam banner */}
      <Box className={classes.banner}>
          <LuShieldCheck size={20} color="#b54708" />
        <Text className={classes.bannerText}>
          <Text component="span" className={classes.bannerBold}>We&apos;ll never spam you.</Text>{' '}
          <Text component="span" className={classes.bannerRegular}>
            You&apos;ll only hear from us about things that matter.
          </Text>
        </Text>
      </Box>

      {/* Channels label */}
      <Text className={classes.channelsLabel}>Communication Channels</Text>

      {/* Channel rows */}
      <Box className={classes.channelsWrap}>
        {/* SMS */}
        <Box className={classes.channelRow}>
          <Box className={classes.channelIcon}>
            <LuMessageCircle size={18} color="#525252" />
          </Box>
          <Box className={classes.channelBody}>
            <Text className={classes.channelTitle}>Text message (SMS)</Text>
            <Text className={classes.channelDesc}>
              A quick text for time-sensitive things, like security codes and urgent account alerts.
            </Text>
          </Box>
          <Switch
            checked={smsEnabled}
            onChange={(e) => setSmsEnabled(e.currentTarget.checked)}
            size="md"
            color="dark"
            disabled={!isAdmin}
            className={classes.toggle}
          />
        </Box>

        {/* Email */}
        <Box className={classes.channelRow}>
          <Box className={classes.channelIcon}>
            <LuMail size={18} color="#525252" />
          </Box>
          <Box className={classes.channelBody}>
            <Text className={classes.channelTitle}>Email</Text>
            <Text className={classes.channelDesc}>
              Account updates, confirmations, and important notices sent to your inbox.
            </Text>
          </Box>
          <Switch
            checked={emailEnabled}
            onChange={(e) => setEmailEnabled(e.currentTarget.checked)}
            size="md"
            color="dark"
            disabled={!isAdmin}
            className={classes.toggle}
          />
        </Box>
      </Box>

      {/* Footer */}
      <Box className={classes.footer}>
        <Text className={classes.footerText}>
          Changes are saved to your account and timestamped for your records.
        </Text>
        {isAdmin && (
          <Button
            className={classes.saveBtn}
            variant="filled"
            onClick={handleSave}
            loading={saving}
          >
            Save changes
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default SmsConsentManager;
